import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaMapMarkerAlt, FaCalendar, FaCamera, FaHeart, FaImages } from 'react-icons/fa';
import { getImageUrl, getPublicEvents } from '../services/api';
import './PublicEventsPage.css';

function PublicEventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [error, setError] = useState('');

  // Only re-fetch when the applied search term, sort, or page changes —
  // not on every keystroke in the input.
  useEffect(() => {
    fetchEvents();
  }, [appliedSearch, sortBy, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getPublicEvents({
        search: appliedSearch,
        sort_by: sortBy,
        page,
        per_page: 12,
      });
      // api interceptor returns response.data directly, so fields are top-level
      setEvents(response.events || []);
      setPagination(response.pagination || null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Impossible de charger les événements. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Setting appliedSearch + page together; useEffect will run once both settle
    setAppliedSearch(searchQuery);
    setPage(1);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setPage(1);
  };

  return (
    <div className="public-events-page">
      {/* Header */}
      <div className="public-events-header" style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1>Découvrir les événements</h1>
        <p>Parcourez les événements publics et retrouvez vos photos</p>
      </div>

      {/* Search & Filters */}
      <div className="public-events-search-card">
        <form onSubmit={handleSearch} className="public-events-search-form">
          {/* Search Input */}
          <div style={{ position: 'relative' }}>
            <FaSearch style={{
              position: 'absolute', left: '1rem', top: '50%',
              transform: 'translateY(-50%)', color: '#64748B', fontSize: '1.2rem'
            }} />
            <input
              type="text"
              placeholder="Rechercher par nom, lieu ou catégorie..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%', padding: '1rem 1rem 1rem 3rem',
                borderRadius: '10px', border: '2px solid var(--border)',
                fontSize: '1rem', transition: 'border-color 0.3s', boxSizing: 'border-box',
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = 'var(--primary)'}
              onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
            />
          </div>

          {/* Sort Select */}
          <select
            value={sortBy}
            onChange={handleSortChange}
            style={{
              padding: '1rem', borderRadius: '10px',
              border: '2px solid var(--border)', fontSize: '1rem', cursor: 'pointer'
            }}
          >
            <option value="created_at">Plus récents</option>
            <option value="name">Nom (A-Z)</option>
            <option value="date">Date de l'événement</option>
          </select>

          {/* Search Button */}
          <button
            type="submit"
            style={{
              padding: '1rem 2rem', borderRadius: '10px', border: 'none',
              background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-strong) 100%)',
              color: 'white', fontSize: '1rem', fontWeight: '600',
              cursor: 'pointer', whiteSpace: 'nowrap'
            }}
          >
            Rechercher
          </button>
        </form>

        {/* Results info */}
        {!loading && pagination && (
          <div style={{ marginTop: '1rem', color: '#64748B', fontSize: '0.95rem' }}>
            {pagination.total} événement{pagination.total !== 1 ? 's' : ''} trouvé{pagination.total !== 1 ? 's' : ''}
            {appliedSearch && ` correspondant à "${appliedSearch}"`}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="error-message" style={{ marginBottom: '1.5rem' }}>{error}</div>
      )}

      {/* Events Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '1rem', color: '#64748B' }}>Chargement des événements...</p>
        </div>
      ) : events.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '4rem', background: '#FFFFFF',
          borderRadius: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
        }}>
          <FaImages style={{ fontSize: '4rem', color: '#64748B', marginBottom: '1rem' }} />
          <h3 style={{ color: '#1F2937', marginBottom: '0.5rem' }}>Aucun événement trouvé</h3>
          <p style={{ color: '#64748B' }}>
            {appliedSearch ? 'Essayez un autre terme de recherche' : "Aucun événement public n'est disponible pour le moment"}
          </p>
        </div>
      ) : (
        <>
          <div className="public-events-grid">
            {events.map((event) => (
              <div
                key={event.id}
                onClick={() => navigate(`/event/${event.id}/browse`)}
                style={{
                  background: '#FFFFFF', borderRadius: '15px', overflow: 'hidden',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                  cursor: 'pointer', transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.05)';
                }}
              >
                {/* Cover */}
                <div style={{
                  width: '100%', height: '200px', position: 'relative',
                  background: 'linear-gradient(135deg, #E8EDFF 0%, var(--primary) 100%)',
                }}>
                  {event.cover_photo ? (
                    <img
                      src={getImageUrl(event.cover_photo)}
                      alt={`Couverture de ${event.name}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div style={{
                      position: 'absolute', top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: '4rem', opacity: 0.5
                    }}>📸</div>
                  )}

                  {/* Category Badge */}
                  <div style={{
                    position: 'absolute', top: '1rem', right: '1rem',
                    background: 'rgba(15,15,18,0.75)', padding: '0.5rem 1rem',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '20px', fontSize: '0.85rem',
                    fontWeight: '600', color: '#FFFFFF'
                  }}>
                    {event.category}
                  </div>
                </div>

                {/* Info */}
                <div style={{ padding: '1.5rem' }}>
                  <h3 style={{
                    margin: '0 0 0.8rem 0', color: '#1F2937',
                    fontSize: '1.3rem', fontWeight: '600'
                  }}>
                    {event.name}
                  </h3>

                  {event.description && (
                    <p style={{
                      margin: '0 0 1rem 0', color: '#64748B', fontSize: '0.95rem',
                      lineHeight: '1.5', display: '-webkit-box',
                      WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                    }}>
                      {event.description}
                    </p>
                  )}

                  <div style={{
                    display: 'flex', flexDirection: 'column', gap: '0.5rem',
                    marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border)'
                  }}>
                    {event.location && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748B', fontSize: '0.9rem' }}>
                        <FaMapMarkerAlt style={{ color: '#4F46E5' }} />
                        {event.location}
                      </div>
                    )}
                    {event.event_date && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748B', fontSize: '0.9rem' }}>
                        <FaCalendar style={{ color: '#4ADE80' }} />
                        {new Date(event.event_date).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'long', day: 'numeric'
                        })}
                      </div>
                    )}
                    {event.photographer?.name && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/photographer/${event.photographer.id}`);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: '#64748B',
                          fontSize: '0.9rem',
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <FaCamera style={{ color: '#C084FC' }} />
                        {event.photographer.name}
                      </button>
                    )}
                  </div>

                  {/* Stats */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem', color: '#64748B' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <FaImages /> {event.photo_count ?? 0} photos
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <FaHeart style={{ color: '#4F46E5' }} /> {event.likes_count ?? 0}
                      </span>
                    </div>
                    <button
                      style={{
                        padding: '0.5rem 1rem', borderRadius: '8px', border: 'none',
                        background: '#4F46E5', color: 'white',
                        fontSize: '0.9rem', fontWeight: '500', cursor: 'pointer'
                      }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/event/${event.id}/browse`); }}
                    >
                      Voir l'événement
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div style={{
              marginTop: '3rem', display: 'flex',
              justifyContent: 'center', gap: '0.5rem'
            }}>
              <button
                onClick={() => setPage(p => p - 1)}
                disabled={!pagination.has_prev}
                style={{
                  padding: '0.8rem 1.5rem', borderRadius: '8px', border: 'none',
                  background: pagination.has_prev ? 'var(--surface)' : 'var(--surface-2)',
                  color: pagination.has_prev ? 'var(--text)' : 'var(--text-faint)',
                  cursor: pagination.has_prev ? 'pointer' : 'not-allowed',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                }}
              >
                Précédent
              </button>

              {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    style={{
                      padding: '0.8rem 1.2rem', borderRadius: '8px', border: 'none',
                      background: page === pageNum ? 'var(--primary)' : 'var(--surface)',
                      color: page === pageNum ? 'white' : 'var(--text)',
                      cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                      fontWeight: page === pageNum ? '600' : 'normal'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => setPage(p => p + 1)}
                disabled={!pagination.has_next}
                style={{
                  padding: '0.8rem 1.5rem', borderRadius: '8px', border: 'none',
                  background: pagination.has_next ? 'var(--surface)' : 'var(--surface-2)',
                  color: pagination.has_next ? 'var(--text)' : 'var(--text-faint)',
                  cursor: pagination.has_next ? 'pointer' : 'not-allowed',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                }}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PublicEventsPage;
